import React, { useCallback, useEffect, useState } from "react";
import { Gavel, Check, X, Loader2 } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const LABELS = { deal: "Deal removed", trust: "On watch", suspension: "Suspended", verification: "Verification rejected" };

export const AdminAppeals = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { setRows((await api.get("/admin/appeals?status=open")).data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (row, grant) => {
    const note = window.prompt(grant
      ? `Note to ${row.businessName} (optional) — this is emailed with the good news:`
      : `Why are you keeping the decision for ${row.businessName}? (emailed to them)`, "");
    if (note === null) return;
    setBusy(row.id);
    try {
      await api.post(`/admin/appeals/${row.id}/decide`, { grant, note });
      toast({ title: grant ? "Appeal granted" : "Decision kept", description: row.businessName });
      await load();
    } catch (e) { toast({ title: e?.response?.data?.detail || "Action failed" }); }
    finally { setBusy(""); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-appeals">
      <h2 className="font-bold text-foreground flex items-center gap-2"><Gavel size={18} className="text-amber-600" /> Appeals ({rows.length})</h2>
      <p className="text-xs text-muted-foreground mt-0.5 mb-3">Shops replying to a removal, watch, suspension or verification rejection — one reply each.</p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="appeals-empty">Nothing to settle.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="border border-border rounded-xl p-3" data-testid={`admin-appeal-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-foreground">{r.businessName}</span>
                <span className="text-[11px] font-bold bg-secondary text-muted-foreground px-2 py-0.5 rounded-full">{LABELS[r.type] || r.type}</span>
                {r.deal?.title && <span className="text-xs text-muted-foreground truncate">· {r.deal.title}</span>}
              </div>
              {r.deal?.hiddenReason && <p className="text-xs text-rose-600 mt-0.5">Our reason: {r.deal.hiddenReason}</p>}
              <p className="text-sm text-foreground mt-2 whitespace-pre-wrap">{r.message}</p>
              <div className="flex items-center gap-2 mt-3">
                <button onClick={() => decide(r, true)} disabled={busy === r.id} data-testid={`appeal-grant-${r.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] text-white rounded-xl px-3 py-2 transition-colors">
                  {busy === r.id ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Grant
                </button>
                <button onClick={() => decide(r, false)} disabled={busy === r.id} data-testid={`appeal-decline-${r.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:bg-secondary rounded-xl px-3 py-2 transition-colors">
                  <X size={13} /> Keep decision
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAppeals;
